import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import { PROJECT_TYPES, BUDGET_RANGES, BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Project", "Details", "About You"];

export default function ContactSection() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    project_type: "",
    budget: "",
    message: "",
    name: "",
    email: "",
    phone: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext =
    (step === 0 && form.project_type) ||
    (step === 1 && form.message.trim().length > 4) ||
    step === 2;

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Inquiry sent! We'll be in touch shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" className="section-y relative bg-[#070707]">
      <div className="container-x">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="overline">Start a Project</span>
              <h2 className="mt-5 font-playfair text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Let's bring your vision to a global audience
              </h2>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-neutral-400">
                Tell us about your project and our team will get back to you with the
                next steps. From concept to worldwide premiere — we're with you.
              </p>

              <ul className="mt-10 space-y-5 text-sm font-light text-neutral-300">
                <li className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-gold">
                    <Phone size={16} />
                  </span>
                  <a href={`tel:${BRAND.phone}`} className="hover:text-white">{BRAND.phone}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-gold">
                    <Mail size={16} />
                  </span>
                  <a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-gold">
                    <MapPin size={16} />
                  </span>
                  {BRAND.address}
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="glass rounded-3xl p-6 sm:p-8 md:p-10">
              {done ? (
                <motion.div
                  data-testid="inquiry-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex min-h-[360px] flex-col items-center justify-center text-center"
                >
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-gold text-black">
                    <Check size={30} />
                  </span>
                  <h3 className="mt-6 font-playfair text-2xl text-white">Thank you!</h3>
                  <p className="mt-3 max-w-sm text-sm font-light text-neutral-400">
                    Your inquiry has been received. Our team will reach out within 1–2 business days.
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="mb-8 flex items-center gap-3">
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex flex-1 items-center gap-3">
                        <div
                          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs transition-all ${
                            i <= step ? "bg-gold text-black" : "border border-white/15 text-neutral-500"
                          }`}
                        >
                          {i < step ? <Check size={16} /> : i + 1}
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`h-px flex-1 ${i < step ? "bg-gold" : "bg-white/10"}`} />
                        )}
                      </div>
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {step === 0 && (
                        <div>
                          <label className="overline">What are you creating?</label>
                          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {PROJECT_TYPES.map((p) => (
                              <button
                                key={p}
                                data-testid={`project-type-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => update("project_type", p)}
                                className={`rounded-xl border px-4 py-3.5 text-left text-sm transition-all ${
                                  form.project_type === p
                                    ? "border-gold bg-gold/10 text-white"
                                    : "border-white/10 text-neutral-300 hover:border-white/30"
                                }`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {step === 1 && (
                        <div className="space-y-6">
                          <div>
                            <label className="overline">Estimated budget</label>
                            <div className="mt-5 flex flex-wrap gap-3">
                              {BUDGET_RANGES.map((b) => (
                                <button
                                  key={b}
                                  data-testid={`budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  onClick={() => update("budget", b)}
                                  className={`rounded-full border px-5 py-2.5 text-sm transition-all ${
                                    form.budget === b
                                      ? "border-gold bg-gold/10 text-white"
                                      : "border-white/10 text-neutral-300 hover:border-white/30"
                                  }`}
                                >
                                  {b}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="overline">Tell us about your project</label>
                            <textarea
                              data-testid="inquiry-message"
                              value={form.message}
                              onChange={(e) => update("message", e.target.value)}
                              rows={5}
                              placeholder="Share your concept, timeline, and goals..."
                              className="mt-4 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-gold"
                            />
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div className="space-y-4">
                          <div>
                            <label className="overline">Full name</label>
                            <input
                              data-testid="inquiry-name"
                              value={form.name}
                              onChange={(e) => update("name", e.target.value)}
                              placeholder="Your name"
                              className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-gold"
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label className="overline">Email</label>
                              <input
                                data-testid="inquiry-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder="you@email.com"
                                className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-gold"
                              />
                            </div>
                            <div>
                              <label className="overline">Phone</label>
                              <input
                                data-testid="inquiry-phone"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                placeholder="Optional"
                                className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-neutral-600 focus:border-gold"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <div className="mt-8 flex items-center justify-between">
                    <button
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0}
                      data-testid="inquiry-back"
                      className={`inline-flex items-center gap-2 text-sm transition-colors ${
                        step === 0 ? "invisible" : "text-neutral-400 hover:text-white"
                      }`}
                    >
                      <ArrowLeft size={16} /> Back
                    </button>

                    {step < 2 ? (
                      <button
                        onClick={() => canNext && setStep((s) => s + 1)}
                        disabled={!canNext}
                        data-testid="inquiry-next"
                        className={`inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-medium transition-all ${
                          canNext ? "bg-white text-black hover:bg-gold" : "cursor-not-allowed bg-white/10 text-neutral-500"
                        }`}
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="inquiry-submit"
                        className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-medium text-black transition-all hover:bg-white disabled:opacity-60"
                      >
                        {submitting ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                        Send Inquiry
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
